import type { ComponentPropsWithRef } from 'react';
import { cn } from '../lib/cn';

/** 22 (numbered step) · 28 (rail) · 30 (user list) · 34 · 54 (inspector card). */
export type AvatarSize = 22 | 28 | 30 | 34 | 54;

/**
 * - `initials`    two letters on a neutral ground
 * - `placeholder` the hatch pattern, standing in for a photo
 * - `accent`      solid accent with white text — a completed onboarding step
 */
export type AvatarVariant = 'initials' | 'placeholder' | 'accent';

export type AvatarProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    size?: AvatarSize;
    variant?: AvatarVariant;
    /** Initials or step number. Omit for `placeholder`. */
    children?: string;
    /** On a dark surface the neutral ground and text flip. */
    onDark?: boolean;
    /**
     * Screen-reader name. An avatar next to a visible name is decoration: leave this
     * out and it is hidden. A standalone avatar must name its person.
     */
    label?: string;
};

const fontSize: Record<AvatarSize, string> = {
    22: 'text-[12px]',
    28: 'text-[12px]',
    30: 'text-[11px]',
    34: 'text-[13px]',
    54: 'text-[16px]',
};

export function Avatar({
    size = 30,
    variant = 'initials',
    onDark = false,
    label,
    className,
    children,
    ...rest
}: AvatarProps) {
    const decorative = !label;

    return (
        <div
            role={decorative ? undefined : 'img'}
            aria-label={label}
            aria-hidden={decorative || undefined}
            className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-full',
                'font-[family-name:var(--font-body)] font-medium leading-none',
                fontSize[size],
                variant === 'placeholder' &&
                    'bg-[image:var(--placeholder-hatch)] border border-[var(--placeholder-border)]',
                variant === 'accent' && 'bg-[var(--accent)] text-[var(--accent-text)]',
                variant === 'initials' &&
                    (onDark
                        ? 'bg-[var(--ink-400)] text-[var(--text-on-dark-2)]'
                        : 'bg-[var(--neutral-tint)] text-[var(--text-secondary)]'),
                className,
            )}
            style={{ width: `${size}px`, height: `${size}px` }}
            {...rest}
        >
            {variant === 'placeholder' ? null : children}
        </div>
    );
}
